import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { listLevels, listParagraphs, looksLikeManualList, readDocument } from "@/office/word/document";

export const lektionListen: Lesson = {
  id: "listen",
  title: "Aufzählungen und Nummerierungen",
  summary: "Echte Listen statt getippter Striche – inklusive mehrerer Ebenen.",
  durationMinutes: 10,
  learningGoals: [
    "Aufzählungen und Nummerierungen anlegen",
    "Mehrstufige Listen mit Tab und Umschalt + Tab gliedern",
    "Nachgetippte Listen als Fehlerquelle erkennen",
  ],
  steps: [
    {
      id: "echte-liste",
      title: "Eine echte Liste anlegen",
      explanation: [
        "Eine Liste lässt sich leicht nachahmen: Bindestrich, Leerzeichen, Text. Sieht " +
          "aus wie eine Aufzählung, ist aber nur Fließtext mit einem Strich davor.",
        "Der Unterschied zeigt sich beim ersten Umbruch. Wird ein Punkt länger als eine " +
          "Zeile, beginnt die zweite Zeile bei einer echten Liste sauber unter dem Text – " +
          "bei einer nachgetippten steht sie unter dem Strich. Und eine Nummerierung " +
          "zählt Word automatisch weiter, auch wenn du mittendrin einen Punkt einfügst.",
        "Die beiden Schaltflächen dafür sitzen auf der Registerkarte „Start“ in der " +
          "Gruppe „Absatz“, ganz links.",
      ],
      keyPoints: [
        "Echte Listen rücken Folgezeilen automatisch richtig ein.",
        "Nummerierungen zählen sich beim Einfügen und Löschen selbst neu.",
        "Word wandelt „1.“ oder „- “ am Zeilenanfang oft automatisch in eine Liste um.",
      ],
      task: {
        instruction:
          "Lege eine Aufzählung oder Nummerierung mit mindestens drei Punkten an – als " +
          "echte Word-Liste, nicht mit getippten Strichen.",
        hint: "Zeilen schreiben, alle markieren, dann auf die Schaltfläche „Aufzählungszeichen“ klicken.",
        check: async () => {
          const snapshot = await readDocument();
          const listen = listParagraphs(snapshot);
          const nachgetippt = snapshot.filledParagraphs.filter(looksLikeManualList);

          if (listen.length === 0 && nachgetippt.length > 0) {
            return fail(
              `${nachgetippt.length} ${
                nachgetippt.length === 1 ? "Absatz sieht" : "Absätze sehen"
              } wie eine Liste aus, ${
                nachgetippt.length === 1 ? "ist" : "sind"
              } aber normaler Text mit einem Zeichen davor.`,
              "Markiere die Zeilen, entferne die getippten Striche und klicke auf „Aufzählungszeichen“.",
            );
          }

          return fromCriteria(
            [
              criterion(
                `Mindestens drei Listenpunkte (gefunden: ${listen.length})`,
                listen.length >= 3,
              ),
              criterion(
                `Keine nachgetippten Listen mehr (gefunden: ${nachgetippt.length})`,
                nachgetippt.length === 0,
              ),
            ],
            {
              success: "Das ist eine echte Liste – Word verwaltet sie ab jetzt für dich.",
              failure: "Die Liste ist noch nicht vollständig.",
              hint: "Blende die Absatzmarken ein (Strg + Umschalt + 8): Bei einer echten Liste gehört das Aufzählungszeichen nicht zum Text und lässt sich nicht anklicken.",
            },
          );
        },
      },
    },
    {
      id: "mehrere-ebenen",
      title: "Mehrere Ebenen gliedern",
      explanation: [
        "Listen können wie Überschriften verschachtelt werden. Der Trick ist denkbar " +
          "einfach: Setze den Cursor an den Anfang eines Listenpunkts und drücke Tab – " +
          "der Punkt rutscht eine Ebene tiefer. Umschalt + Tab holt ihn wieder herauf.",
        "Word passt das Aufzählungszeichen jeder Ebene automatisch an und rückt sie " +
          "richtig ein. Bei Nummerierungen entsteht so die vertraute Zählung 1., a), i.",
        "Wichtig ist die Reihenfolge: Tab wirkt nur am Zeilenanfang. Steht der Cursor " +
          "mitten im Text, fügt Word einen Tabulator ein statt die Ebene zu wechseln.",
      ],
      keyPoints: [
        "Tab am Zeilenanfang: eine Ebene tiefer. Umschalt + Tab: eine Ebene höher.",
        "Für nummerierte Gliederungen: „Liste mit mehreren Ebenen“ in der Gruppe „Absatz“.",
        "Mehr als drei Ebenen werden für Leserinnen und Leser schnell unübersichtlich.",
      ],
      task: {
        instruction:
          "Gliedere deine Liste: Mindestens ein Punkt soll auf einer tieferen Ebene stehen " +
          "als die anderen.",
        hint: "Cursor an den Anfang des Listenpunkts setzen und einmal Tab drücken.",
        check: async () => {
          const snapshot = await readDocument();
          const listen = listParagraphs(snapshot);
          const ebenen = listLevels(snapshot);

          if (listen.length === 0) {
            return fail(
              "Es gibt noch keine Liste im Dokument.",
              "Lege zuerst wie im vorherigen Schritt eine Aufzählung an.",
            );
          }
          if (ebenen.length < 2) {
            return fail(
              "Alle Listenpunkte stehen auf derselben Ebene.",
              "Der Cursor muss ganz am Anfang der Zeile stehen, damit Tab die Ebene wechselt.",
            );
          }
          return pass(
            `Deine Liste nutzt ${ebenen.length} Ebenen – genau so entsteht eine Gliederung.`,
          );
        },
      },
    },
  ],
};
